/**
 * Transport adapters for the live channel (`GET /live/v1/stream`, upgrade:
 * WebSocket, or `?transport=sse` for Server-Sent Events — endpoint catalog).
 *
 * The hooks that consume this ({@link useConnectionHealth},
 * `usePlayArchiveSubscription`) only ever see {@link HealthSocket}: four
 * callbacks and two methods. That keeps the browser `WebSocket` and
 * `EventSource` out of the state machine and makes the whole heartbeat
 * drivable from a test with a hand-written fake.
 */

import { STORAGE_KEYS } from './constants';
import { loadSession } from './session-storage';

/** WebSocket/SSE endpoint from the endpoint catalog. */
export const LIVE_STREAM_PATH = '/live/v1/stream';

/**
 * Query parameters the gateway accepts on STREAM routes only
 * (`services/gateway/src/pipeline.ts`): a browser cannot set the
 * `Authorization` header on a WebSocket upgrade or an `EventSource`, so the
 * bearer travels as `?access_token=` and — for personas whose token carries no
 * `tid` claim (fanatic) — the team context travels as `?tenant_id=`.
 */
export const ACCESS_TOKEN_QUERY_PARAM = 'access_token';
export const TENANT_ID_QUERY_PARAM = 'tenant_id';

export interface LiveStreamAuth {
  accessToken: string | null;
  tenantId: string | null;
}

/**
 * Read the signed-in session's credentials for the live stream. Fanatic
 * sessions have no tenant on the token, so the persisted team selection
 * (`STORAGE_KEYS.fanaticTeam`, written by the session provider) supplies it.
 */
export function resolveLiveStreamAuth(): LiveStreamAuth {
  const session = loadSession();
  if (session === null) return { accessToken: null, tenantId: null };
  let tenantId = session.tenantId;
  if (tenantId === null) {
    try {
      const raw = globalThis.localStorage?.getItem(STORAGE_KEYS.fanaticTeam);
      const parsed = typeof raw === 'string' ? (JSON.parse(raw) as { tenantId?: unknown }) : null;
      if (typeof parsed?.tenantId === 'string' && parsed.tenantId !== '') {
        tenantId = parsed.tenantId;
      }
    } catch {
      // Storage denied or malformed selection: connect tenant-less; the
      // gateway answers with a structured 400 rather than a silent hang.
    }
  }
  return { accessToken: session.accessToken, tenantId };
}

/**
 * Append fresh credentials to a stream URL. Called at CONNECT time (not when
 * the URL is memoised) so every reconnect attempt carries the current token,
 * surviving silent refresh. A URL that already names the parameters — or a
 * runtime with no session — is left untouched.
 */
export function appendLiveStreamAuth(
  url: string,
  auth: LiveStreamAuth = resolveLiveStreamAuth()
): string {
  if (auth.accessToken === null) return url;
  try {
    const parsed = new URL(url);
    parsed.searchParams.set(ACCESS_TOKEN_QUERY_PARAM, auth.accessToken);
    if (auth.tenantId !== null && !parsed.searchParams.has(TENANT_ID_QUERY_PARAM)) {
      parsed.searchParams.set(TENANT_ID_QUERY_PARAM, auth.tenantId);
    }
    return parsed.toString();
  } catch {
    return url;
  }
}

/** Minimal transport contract — a real `WebSocket` is adapted onto this. */
export interface HealthSocket {
  send: (data: string) => void;
  close: () => void;
}

export interface HealthSocketHandlers {
  onOpen: () => void;
  onClose: () => void;
  onError: () => void;
  onMessage: (data: string) => void;
}

export type HealthSocketFactory = (
  url: string,
  handlers: HealthSocketHandlers
) => HealthSocket | null;

/** Same default as `resolveApiBaseUrl()`, without importing the API client. */
export function resolveApiBase(): string {
  const configured = import.meta.env?.VITE_API_BASE_URL;
  return typeof configured === 'string' && configured !== ''
    ? configured.replace(/\/$/, '')
    : '/api';
}

/** `/api` + `/live/v1/stream` → `ws(s)://host/api/live/v1/stream?event_id=…`. */
export function resolveLiveSocketUrl(
  baseUrl: string,
  eventId: string | null,
  origin?: string
): string {
  const base = origin ?? globalThis.location?.origin ?? 'http://localhost';
  const url = new URL(`${baseUrl}${LIVE_STREAM_PATH}`, base);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  if (eventId !== null && eventId !== '') url.searchParams.set('event_id', eventId);
  return url.toString();
}

/**
 * `/api` + `/live/v1/stream` → `http(s)://host/api/live/v1/stream?transport=sse…`.
 *
 * `EventSource` cannot set an `Accept` header, so the SSE branch of the live
 * gateway is selected with the `transport=sse` query parameter
 * (`services/live/src/sse-handler.ts`).
 */
export function resolveLiveSseUrl(
  baseUrl: string,
  eventId: string | null,
  origin?: string
): string {
  const base = origin ?? globalThis.location?.origin ?? 'http://localhost';
  const url = new URL(`${baseUrl}${LIVE_STREAM_PATH}`, base);
  url.searchParams.set('transport', 'sse');
  if (eventId !== null && eventId !== '') url.searchParams.set('event_id', eventId);
  return url.toString();
}

/**
 * Adapts the browser `WebSocket`. Returns `null` when no socket is available or
 * we are inside a unit-test environment — tests inject their own transport, and
 * jsdom must never dial a real host.
 */
export function createLiveSocket(url: string, handlers: HealthSocketHandlers): HealthSocket | null {
  if (import.meta.env?.MODE === 'test') return null;
  const SocketCtor = globalThis.WebSocket;
  if (typeof SocketCtor !== 'function') return null;
  const socket = new SocketCtor(appendLiveStreamAuth(url));
  socket.onopen = () => handlers.onOpen();
  socket.onclose = () => handlers.onClose();
  socket.onerror = () => handlers.onError();
  socket.onmessage = (event: MessageEvent<unknown>) =>
    handlers.onMessage(typeof event.data === 'string' ? event.data : '');
  return {
    send: (data) => socket.send(data),
    close: () => socket.close(),
  };
}

/**
 * SSE event names written by the live gateway (`event: ${frame.type}` in
 * `services/live/src/sse-handler.ts`). `EventSource` has no wildcard listener,
 * so the adapter must subscribe to every name in the catalog. Mirror of
 * `LIVE_MESSAGE` + `LIVE_CONTROL` in `packages/shared/src/live-messages.ts` —
 * restated here because the browser bundle deliberately does not depend on the
 * service kit (see the note in `live-client.ts`).
 */
export const LIVE_SSE_EVENT_TYPES = [
  'game.play.created',
  'game.play.updated',
  'game.play.deleted',
  'game.play.version_added',
  'game.play.conflict_detected',
  'game.play.conflict_resolved',
  'stats.updated',
  'event.updated',
  'event.closed',
  'event.reopened',
  'event.participation.updated',
  'connection.ready',
  'connection.error',
] as const;

/** The subset of `EventSource` the adapter needs — injectable in tests. */
export interface EventSourceLike {
  addEventListener: (type: string, listener: (event: { data?: unknown }) => void) => void;
  close: () => void;
}

/**
 * Adapts an `EventSource` (or an injected stand-in) onto {@link HealthSocket}.
 *
 * SSE is one-way, so `send` cannot put a ping on the wire. Instead the adapter
 * answers each `{ type: 'ping' }` frame locally with a synthetic pong while the
 * stream is open: over this transport, "healthy" means "the event stream is
 * open and delivering", which is the strongest liveness signal a one-way
 * transport offers. A dead connection still surfaces — `EventSource` fires
 * `error` when the stream drops, and the adapter reports it as a close so the
 * caller's reconnect policy (not the browser's opaque auto-retry) owns
 * recovery.
 */
export function adaptEventSource(
  source: EventSourceLike,
  handlers: HealthSocketHandlers
): HealthSocket {
  let isClosed = false;
  source.addEventListener('open', () => {
    if (!isClosed) handlers.onOpen();
  });
  source.addEventListener('error', () => {
    if (isClosed) return;
    isClosed = true;
    source.close();
    handlers.onError();
    handlers.onClose();
  });
  for (const type of LIVE_SSE_EVENT_TYPES) {
    source.addEventListener(type, (event) => {
      if (!isClosed) handlers.onMessage(typeof event.data === 'string' ? event.data : '');
    });
  }
  return {
    send: (data) => {
      if (isClosed) return;
      const frame = parseLiveFrame(data);
      if (frame?.type !== 'ping') return;
      const id = frame.payload?.id;
      setTimeout(() => {
        if (isClosed) return;
        handlers.onMessage(
          JSON.stringify({
            type: 'pong',
            payload: { id: typeof id === 'string' ? id : null },
            traceId: null,
          })
        );
      }, 0);
    },
    close: () => {
      if (isClosed) return;
      isClosed = true;
      source.close();
    },
  };
}

/**
 * Adapts the browser `EventSource` — the fallback transport for networks that
 * block WebSockets. Returns `null` when no `EventSource` is available or we
 * are inside a unit-test environment, mirroring {@link createLiveSocket}.
 */
export function createLiveSseSocket(
  url: string,
  handlers: HealthSocketHandlers
): HealthSocket | null {
  if (import.meta.env?.MODE === 'test') return null;
  const SourceCtor = globalThis.EventSource;
  if (typeof SourceCtor !== 'function') return null;
  return adaptEventSource(
    new SourceCtor(appendLiveStreamAuth(url), { withCredentials: true }),
    handlers
  );
}

interface LiveFrame {
  type?: unknown;
  payload?: { id?: unknown } | null;
}

/** Parse one `{ type, payload, traceId }` frame; `null` when it is not JSON. */
export function parseLiveFrame(data: string): LiveFrame | null {
  try {
    const parsed: unknown = JSON.parse(data);
    return typeof parsed === 'object' && parsed !== null ? (parsed as LiveFrame) : null;
  } catch {
    return null;
  }
}
